import { useEffect, useState } from 'react';
import { api, clearEmailLog } from '../../lib/api';

interface LogEntry {
  id: string;
  template_slug: string;
  recipient_email: string;
  status: string;
  sent_at: string | null;
  error_message: string | null;
  created_at: string;
}

const statusColors: Record<string, string> = {
  sent: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
  pending: 'bg-yellow-100 text-yellow-800',
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('nl-NL');
}

export default function EmailLog() {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [clearing, setClearing] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);

  useEffect(() => { fetchLogs(); }, []);

  async function fetchLogs() {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get<{ logs: LogEntry[] }>('/email/log');
      setLogs(data.logs ?? []);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  if (loading) return <div className="p-8 text-center text-gray-500">E-mail log laden...</div>;

  const sentCount = logs.filter(l => l.status === 'sent').length;
  const failedCount = logs.filter(l => l.status === 'failed').length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">E-mail Log</h1>
        <div className="flex items-center gap-2">
          {logs.length > 0 && !confirmClear && (
            <button onClick={() => setConfirmClear(true)} className="btn-secondary text-sm text-red-600 hover:bg-red-50">
              Log wissen
            </button>
          )}
          {confirmClear && (
            <>
              <span className="text-sm text-red-600">Weet je het zeker?</span>
              <button
                onClick={async () => { setClearing(true); try { await clearEmailLog(); setLogs([]); } catch {} finally { setClearing(false); setConfirmClear(false); } }}
                disabled={clearing}
                className="btn-secondary text-sm text-red-700 bg-red-50 hover:bg-red-100"
              >
                {clearing ? 'Wissen...' : 'Ja, wissen'}
              </button>
              <button onClick={() => setConfirmClear(false)} className="btn-secondary text-sm">Annuleren</button>
            </>
          )}
          <button onClick={fetchLogs} className="btn-secondary">Vernieuwen</button>
        </div>
      </div>

      {error && <div className="p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">{error}</div>}

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="card p-4">
          <div className="text-2xl font-bold text-gray-900">{logs.length}</div>
          <div className="text-sm text-gray-500">Totaal</div>
        </div>
        <div className="card p-4">
          <div className="text-2xl font-bold text-green-600">{sentCount}</div>
          <div className="text-sm text-gray-500">Verstuurd</div>
        </div>
        <div className="card p-4">
          <div className="text-2xl font-bold text-red-600">{failedCount}</div>
          <div className="text-sm text-gray-500">Mislukt</div>
        </div>
      </div>

      {/* Log table */}
      <div className="card">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Template</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ontvanger</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Verstuurd</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fout</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {logs.map(log => (
                <tr key={log.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{log.template_slug}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{log.recipient_email}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${statusColors[log.status] ?? 'bg-gray-100 text-gray-600'}`}>
                      {log.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {log.sent_at ? formatDate(log.sent_at) : '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-red-500 truncate max-w-xs">
                    {log.error_message ?? ''}
                  </td>
                </tr>
              ))}
              {logs.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-500 text-sm">Nog geen e-mails verstuurd</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
